import { Search, User, Upload, Menu, X, LogOut, Flame, Coins, Compass } from "lucide-react";
import TikTokIcon from "@/components/TikTokIcon";
import CopyrightNotificationBell from "@/components/CopyrightNotificationBell";
import FriendRequestBell from "@/components/FriendRequestBell";
import { NAVBAR_TARGET_ID } from "@/components/PointsRewardAnimation";
import QuestChecklist from "@/components/QuestChecklist";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import EarlyAdopterBanner from "@/components/EarlyAdopterBanner";
import logoImg from "@/assets/logo.png";
import { Button } from "@/components/ui/button";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";

const VERSION = "BETA 1.1";
const VERSION_LOGS = [
  { version: "V1.1", date: "Apr 9, 2026", changes: [
    "Trendsetter Badge for early adopters",
    "Early adopter announcement banner",
    "Venue section tags on uploads",
    "Edit venue on your own posts",
    "Post deletion",
    "\"I Was There\" badge on posts",
    "🔒 Significant security patches",
  ]},
  { version: "V1.02", date: "Mar 31, 2026", changes: [
    "Improved mobile functionality",
    "Better search functionality",
    "Refer-a-friend feature",
    "Friends / For You tabs",
    "Hover volume toggle on Trending Now",
  ]},
  { version: "V1.01", date: "Mar 19, 2026", changes: [
    "Significant speed and accessibility fixes",
  ]},
  { version: "V1.0", date: "Mar 18, 2026", changes: [
    "Launch of Encore Republic 🎉",
    "Concert history tracking with Setlist.fm integration",
    "Clip uploads with video & photo support",
    "Republic Points gamification system",
    "Friend requests & friends-only leaderboard",
    "Personalized artist & show recommendations",
    "Ticketmaster event detail pages",
    "Artist fan system with trending discovery",
    "Near You geolocation-based show filtering",
    "Profile sharing with public URLs",
  ]},
];

const NAV_ITEMS = [
  { to: "/", label: "Discover", icon: Compass },
  { to: "/search", label: "Search", icon: Search },
  { to: "/profile", label: "Profile", icon: User },
  { to: "/upload", label: "Upload", icon: Upload },
];

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [versionOpen, setVersionOpen] = useState(false);
  const [republicPoints, setRepublicPoints] = useState(() => {
    const cached = sessionStorage.getItem("republic_points");
    return cached ? Number(cached) : 0;
  });
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  useEffect(() => {
    let active = true;

    const loadPoints = async () => {
      if (!user) {
        setRepublicPoints(0);
        return;
      }

      const { data } = await supabase
        .from("profiles")
        .select("republic_points")
        .eq("user_id", user.id)
        .maybeSingle();

      if (!active) return;
      const pts = Number((data as { republic_points?: number } | null)?.republic_points ?? 0);
      setRepublicPoints(pts);
      sessionStorage.setItem("republic_points", String(pts));
    };

    loadPoints();

    return () => {
      active = false;
    };
  }, [user]);

  const handleAuth = () => {
    if (user) {
      signOut();
    } else {
      navigate("/auth");
    }
  };

  const navRef = useRef<HTMLElement>(null);

  const updateNavHeight = useCallback(() => {
    if (navRef.current) {
      document.documentElement.style.setProperty(
        "--navbar-height",
        `${navRef.current.offsetHeight}px`
      );
    }
  }, []);

  useEffect(() => {
    updateNavHeight();
    const observer = new ResizeObserver(updateNavHeight);
    if (navRef.current) observer.observe(navRef.current);
    return () => observer.disconnect();
  }, [updateNavHeight]);

  return (
    <nav ref={navRef} className="fixed top-0 left-0 right-0 z-50 bg-background/60 backdrop-blur-2xl border-b border-white/10">
      <EarlyAdopterBanner />
      <div className="container mx-auto flex h-18 md:h-20 items-center justify-between px-4 min-h-[72px]">
        <div className="flex items-center gap-1.5">
          <Link to="/" className="flex items-center gap-2 group">
            <motion.img
              src={logoImg}
              alt="Encore Republic"
              width={200}
              height={40}
              className="h-10 object-contain"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400 }}
            />
          </Link>
          <button
            onClick={() => setVersionOpen(true)}
            className="text-[10px] font-body text-muted-foreground/60 hover:text-primary transition-colors mt-1 self-end"
          >
            {VERSION}
          </button>
          <a
            href="https://www.tiktok.com/@encorerepublic"
            target="_blank"
            rel="noopener noreferrer"
            className="ml-1 text-muted-foreground hover:text-primary transition-colors"
            aria-label="TikTok"
          >
            <TikTokIcon className="h-5 w-5" />
          </a>
        </div>

        {/* Version Log Dialog */}
        <Dialog open={versionOpen} onOpenChange={setVersionOpen}>
          <DialogContent className="sm:max-w-md bg-card border-border">
            <DialogHeader>
              <DialogTitle className="font-display text-foreground">Version History</DialogTitle>
            </DialogHeader>
            <div className="space-y-6 max-h-[60vh] overflow-y-auto">
              {VERSION_LOGS.map((log) => (
                <div key={log.version}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-display font-bold text-foreground">{log.version}</span>
                    <span className="text-xs text-muted-foreground font-body">{log.date}</span>
                  </div>
                  <ul className="space-y-1.5">
                    {log.changes.map((change, i) => (
                      <li key={i} className="text-sm text-muted-foreground font-body flex items-start gap-2">
                        <span className="text-primary mt-0.5">•</span>
                        {change}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        <div className="hidden md:flex items-center gap-1 bg-secondary/50 rounded-full px-1.5 py-1">
          {NAV_ITEMS.map((item) => {
            const isActive = location.pathname === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`relative flex items-center gap-2 px-4 py-2 rounded-full text-sm font-display font-medium transition-all duration-300 ${
                  isActive
                    ? "text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {isActive && (
                  <motion.div
                    layoutId="nav-pill"
                    className="absolute inset-0 gradient-hero rounded-full"
                    transition={{ type: "spring", stiffness: 350, damping: 30 }}
                  />
                )}
                <span className="relative z-10 flex items-center gap-2">
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </span>
              </Link>
            );
          })}
        </div>

        <div className="flex items-center gap-2">
          {user && (
            <>
              <FriendRequestBell />
              <CopyrightNotificationBell />
              <QuestChecklist />
              <Link to="/points" id={NAVBAR_TARGET_ID} className="hidden md:flex items-center gap-1.5 rounded-full border border-border bg-secondary/50 px-3 h-9 hover:border-primary/30 hover:bg-primary/5 transition-all">
                <Coins className="h-4 w-4 text-primary" />
                <span className="text-sm font-display font-semibold text-foreground">{republicPoints.toLocaleString()}</span>
                <span className="hidden lg:inline text-xs text-muted-foreground">Republic Points</span>
              </Link>
            </>
          )}
          <Button
            size="sm"
            className={`font-display font-semibold hidden sm:inline-flex rounded-full ${
              user
                ? "border-border bg-secondary/50 hover:bg-secondary text-foreground"
                : "gradient-hero border-0 shadow-lg shadow-primary/20"
            }`}
            variant={user ? "outline" : "default"}
            onClick={handleAuth}
          >
            {user ? (
              <><LogOut className="mr-1.5 h-4 w-4" />Sign Out</>
            ) : (
              <><Flame className="mr-1.5 h-4 w-4" />Join Free</>
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden text-foreground"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="md:hidden overflow-hidden glass-heavy"
          >
            <div className="container mx-auto px-4 py-4 flex flex-col gap-2">
              {user && (
                <Link
                  to="/points"
                  onClick={() => setMobileOpen(false)}
                  className="mb-1 flex items-center justify-between rounded-xl border border-border bg-secondary/50 px-4 py-3 hover:border-primary/30 transition-all"
                >
                  <span className="text-sm font-display text-muted-foreground">Republic Points</span>
                  <span className="flex items-center gap-1.5 text-sm font-display font-semibold text-foreground">
                    <Coins className="h-4 w-4 text-primary" />
                    {republicPoints.toLocaleString()}
                  </span>
                </Link>
              )}

              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 rounded-xl px-4 py-3.5 font-display font-medium transition-all duration-200 ${
                    location.pathname === item.to
                      ? "gradient-hero text-primary-foreground shadow-lg shadow-primary/20"
                      : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                  }`}
                >
                  <item.icon className="h-5 w-5" />
                  {item.label}
                </Link>
              ))}
              <Button
                className={`mt-2 font-display font-semibold h-12 text-base rounded-xl ${user ? "" : "gradient-hero border-0"}`}
                variant={user ? "outline" : "default"}
                onClick={() => { setMobileOpen(false); handleAuth(); }}
              >
                {user ? "Sign Out" : "Join Free"}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;

